import CarouselViewAllLink from 'components/Common/CarouselViewAllLink'
import Carousel from 'LuxKit/Carousel/Carousel'
import FilterChip from 'LuxKit/Chips/FilterChip'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyText, { BodyTextLarge } from 'LuxKit/Typography/BodyText'
import Heading, { Heading2Style } from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { trackClientEvent } from 'analytics/analytics'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useOfferListCarouselDisplayLimit from 'components/OfferList/useOfferListCarouselDisplayLimit'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { LabFeatureFlags } from 'constants/lab'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import GeoContext from 'contexts/geoContext'
import useHomepageOfferCarouselList from 'hooks/Offers/useHomepageOfferCarouselList'
import { buildHomepageHeroOfferListFilters } from 'home/helpers'
import type { HeroOfferCarouselFilterOption } from 'home/helpers'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import useLereRankedOfferList from 'hooks/Offers/useLereRankedOfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import { isHeroCarouselPayload, isLabCampaignTagPayload } from 'lib/lab/dynamicPayload'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { rem } from 'polished'
import { useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { GenericBackgroundImage } from 'takeovers/utils/components/GenericBackgroundImage'
import { GenericVideoBackground } from 'takeovers/utils/components/GenericVideoBackground'
import SiteTakeoverBackgroundImage from 'takeovers/utils/components/SiteTakeoverBackgroundImage'
import { SiteTakeoverCampaignTagLabel } from 'takeovers/utils/components/SiteTakeoverCampaignTagLabel'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import type { IconPlacementRule, ImageAttachment } from '@luxuryescapes/contract-svc-lab'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useIntl } from 'react-intl'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import {
  getExclusiveOfferCarouselSurface,
  hasCustomHeroOfferCarouselSurface,
} from './heroOfferCarouselSurface'

const SiteTakeoverBackground = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
`

const CarouselContainer = styled(Pane)`
  position: relative;
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
  ${mediaQueryUp.desktop} {
    /**
     * This adds room for the search section on all homepages
     */
    padding-bottom: ${rem(100)};

  }

  /*
   * Teal = Highlights / Primary / DarkBackground.
   * Dual gate: body.app-palette-rebrand (usePaletteVersionBodyClass layout-sync)
   * AND .rebrand-highlight (structural flag). Both read useRebrandVersion() so
   * OPTIMIZELY_* env overrides stay aligned with ThemeProvider before paint.
   *
   * Use &&.class (not nested && inside body…) — nested && duplicates the full
   * compound selector without a comma and never matches.
   */
  body.${REBRAND_PALETTE_CLASS} &&.rebrand-highlight {
    background-color: var(--palette-highlight-primary-dark-background);
  }
`

const Content = styled(VerticalSpacer)`
  position: relative;
  z-index: 1;
`

const StyledHeading2MarkdownRender = styled(MarkdownRender)`
  line-height: inherit;
  color: inherit;
  ${Heading2Style}
`

const StyledBodyTextMarkdownRender = styled(MarkdownRender)`
  line-height: inherit;
  color: inherit;
  ${BodyTextLarge}
`

const InlineBodyTextMarkdownRender = styled(MarkdownRender)`
  display: inline;
  line-height: inherit;
  color: inherit;
  ${BodyTextLarge}

  p {
    display: inline;
    margin: 0;
  }
`

const UNAVAILABLE_HERO_HOLIDAY_TYPE_FILTERS = new Set(['Dining & Drinks'])

function isAvailableHeroFilterOption(option: HeroOfferCarouselFilterOption): boolean {
  return option.type !== 'holidayType' || !UNAVAILABLE_HERO_HOLIDAY_TYPE_FILTERS.has(option.value)
}

interface HeroOfferCarouselTakeoverPayload {
  carouselPalette?: App.Design.PaletteType
  palette?: App.Design.PaletteType
  title?: React.ReactNode
  subtitle?: React.ReactNode | string
  subtitleActionLink?: string
  logo?: React.ReactNode
  background?: React.ReactNode
  attachments?: Array<ImageAttachment>
  videoSourceUrl?: string
  videoThumbnailImageId?: string
  filterOptions: Array<HeroOfferCarouselFilterOption>
  campaignId?: string
  offerListRanking?: NonNullable<App.SiteTakeoverConfig['heroSection']>['offerListRanking']
  iconDimension?: { width?: string; height?: string }
  iconPlacement?: { left?: IconPlacementRule, right?: IconPlacementRule }
}

function transformTakeoverPayload(takeoverPayload: App.SiteTakeoverConfig['heroSection'] | App.LabHeroCarouselPayload, defaultFilterOptions: Array<HeroOfferCarouselFilterOption>): HeroOfferCarouselTakeoverPayload {
  if (isHeroCarouselPayload(takeoverPayload)) {
    return {
      title: takeoverPayload.title ?
          <StyledHeading2MarkdownRender
          // Force heading 2 level for markdown render
            content={'## ' + takeoverPayload.title}
            fontSize="large"
          />
        : undefined,
      subtitle: takeoverPayload.subtitle,
      subtitleActionLink: takeoverPayload.subtitleActionLink,
      filterOptions: takeoverPayload.filters ?? defaultFilterOptions,
      campaignId: takeoverPayload.campaignId,
      videoSourceUrl: takeoverPayload.videoSourceUrl,
      videoThumbnailImageId: takeoverPayload.videoThumbnail,
      attachments: takeoverPayload.attachments,
      iconDimension: takeoverPayload.iconDimension,
      iconPlacement: takeoverPayload.iconPlacement,
    }
  }

  return {
    carouselPalette: takeoverPayload?.carouselPalette,
    palette: takeoverPayload?.palette,
    title: takeoverPayload?.title,
    subtitle: takeoverPayload?.subtitle,
    subtitleActionLink: takeoverPayload?.subtitleActionLink,
    logo: takeoverPayload?.logo,
    background: <SiteTakeoverBackgroundImage background="hero" />,
    filterOptions: takeoverPayload?.filters ?? defaultFilterOptions,
    campaignId: takeoverPayload?.campaignId,
    offerListRanking: takeoverPayload?.offerListRanking,
  }
}

interface Props {
  className?: string
  overrideTitle?: React.ReactNode
  overrideSubtitle?: React.ReactNode
  overrideOfferList?: App.OfferList
  lereHeroCarouselEnabled?: boolean
}

function HeroOfferCarouselSection(props: Props) {
  const { className, overrideTitle, overrideSubtitle, overrideOfferList, lereHeroCarouselEnabled } = props
  const { currentRegionCode, geoStateName } = useContext(GeoContext)
  const intl = useIntl()

  const defaultFilterOptions: Array<HeroOfferCarouselFilterOption> = useMemo(() => [
    {
      label: intl.formatMessage({
        id: 'KIRqWlCYNDUNY7LOfwoR',
        defaultMessage: 'Bonus Dining & Drinks',
        description: 'Holiday type filter label for dining and drinks offers',
      }),
      value: 'Dining & Drinks',
      type: 'holidayType',
    },
    {
      label: intl.formatMessage({
        id: 'leQ6DiKQH4Qi4yKtgdRN',
        defaultMessage: 'Family Friendly',
        description: 'Holiday type filter label for family friendly offers',
      }),
      value: 'Family Friendly',
      type: 'holidayType',
    },
    {
      label: intl.formatMessage({
        id: '8eat2U92L7hJwweSBEAi',
        defaultMessage: 'Sun & Beach',
        description: 'Holiday type filter label for sun and beach offers',
      }),
      value: 'Sun & Beach',
      type: 'holidayType',
    },
  ], [intl])

  const [selectedFilter, setSelectedFilter] = useState<HeroOfferCarouselFilterOption | undefined>()

  // Current site takeover implementation
  const siteTakeover = useCurrentSiteTakeover({ heroVertical: 'home' })

  const siteTakeoverTheme = useSiteTakeoverTheme()

  // Hero carousel takeover payload from svc-lab
  const enableHeroCarouselTakeover = useLabFeatureFlag(LabFeatureFlags.enableHeroCarouselTakeover)
  const heroCarouselTakeoverPayload: App.LabHeroCarouselPayload | undefined = useMemo(() => {
    if (enableHeroCarouselTakeover?.enabled) {
      const payload = enableHeroCarouselTakeover.value
      if (isHeroCarouselPayload(payload)) return payload
    }
    return undefined
  }, [enableHeroCarouselTakeover])

  // Offer campaign tag label payload from svc-lab
  const enableOfferCampaignTag = useLabFeatureFlag(LabFeatureFlags.enableOfferCampaignTag)
  const offerCampaignTagPayload: App.LabCampaignTagPayload | undefined = useMemo(() => {
    if (enableOfferCampaignTag?.enabled) {
      const payload = enableOfferCampaignTag.value
      if (isLabCampaignTagPayload(payload)) {
        return payload
      }
    }
  }, [enableOfferCampaignTag])

  const heroCarouselConfig = useMemo(() => {
    if (siteTakeover?.heroSection) {
      return transformTakeoverPayload(siteTakeover.heroSection, defaultFilterOptions)
    } else if (heroCarouselTakeoverPayload) {
      return transformTakeoverPayload(heroCarouselTakeoverPayload, defaultFilterOptions)
    }
    return undefined
  }, [heroCarouselTakeoverPayload, siteTakeover, defaultFilterOptions])

  const filterOptions = useMemo(() => {
    return heroCarouselConfig?.filterOptions.filter(isAvailableHeroFilterOption)
  }, [heroCarouselConfig?.filterOptions])

  const toggleFilter = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const filter = e.currentTarget.dataset.value!
    const nextFilterValue = selectedFilter?.value === filter ? undefined : filter
    const nextFilter = filterOptions && nextFilterValue ? filterOptions.find(option => option.value === nextFilterValue) : undefined

    if (nextFilterValue) {
      trackClientEvent({
        subject: 'click_button',
        action: filter,
        type: 'interaction',
        category: 'homepage',
      })
    }
    setSelectedFilter(nextFilter)
  }, [selectedFilter, filterOptions])

  const filters = useMemo<App.OfferListFilters>((): App.OfferListFilters => {
    return buildHomepageHeroOfferListFilters({
      region: currentRegionCode,
      subregion: geoStateName,
      campaignId: heroCarouselConfig?.campaignId,
      selectedFilter,
    })
  }, [currentRegionCode, geoStateName, selectedFilter, heroCarouselConfig])

  const shouldRankOfferList = !!lereHeroCarouselEnabled && heroCarouselConfig?.offerListRanking === 'lere-ranker'
  const rankedOfferList = useLereRankedOfferList(filters, {
    disabled: !shouldRankOfferList,
  })

  const searchUrl = useMemo(() => {
    const queryParams = {
      searchItem: ANYWHERE_SEARCH_ITEM,
      rooms: [config.search.defaultOccupants],
    }

    const query = encodeSearchParams(queryParams)
    return `/search?${query.toString()}`
  }, [])

  // If there is a campaignId filter from the takeover config, we don't want to override the filters and offers with the LERE ones
  const canLEREOverride = heroCarouselConfig?.campaignId == null

  // Prioritise ranking if explicitly declared and enabled
  const carouselOverrideOfferList = shouldRankOfferList ? rankedOfferList : canLEREOverride ? overrideOfferList : undefined
  const fetchedList = useOfferList(filters, {
    disabled: !!carouselOverrideOfferList,
  })
  const sourceOfferList = carouselOverrideOfferList ?? fetchedList
  const maxDisplayCount = useOfferListCarouselDisplayLimit()
  const overrideOfferListFetching = !!sourceOfferList.fetching

  // The above-the-fold hero can SSR with a non-blocking offer prefetch. Keep it mounted
  // when a sparse list resolves after hydration to avoid a large layout shift and LCP regression.

  const palette = siteTakeoverTheme?.palette ?? heroCarouselConfig?.palette ?? 'inverse'
  const carouselPalette = siteTakeoverTheme?.palette ?? heroCarouselConfig?.carouselPalette
  const hasCustomHeroSurface = hasCustomHeroOfferCarouselSurface({
    siteTakeoverTheme,
    heroCarouselConfig,
  })
  // Same source as ThemeProvider / body class (includes OPTIMIZELY_* env override).
  const {
    isRebrandExclusiveSurface,
    containerPalette,
    titleSheetPalette,
    carouselSheetPalette,
    tileStyle,
  } = getExclusiveOfferCarouselSurface({
    isRebrand: useRebrandVersion() === 'rebrand',
    hasCustomHeroSurface,
    palette,
    carouselPalette,
  })

  const defaultTitle = useMemo(() => intl.formatMessage(
    {
      id: '4aBS2nn8etyLw8EXBpIW',
      defaultMessage: 'Today’s top <italic>exclusive</italic> offers',
      description: 'Default title for hero offer carousel section on the homepage',
    },
    {
      italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  ), [intl])
  const title = canLEREOverride ? overrideTitle ?? heroCarouselConfig?.title ?? defaultTitle : heroCarouselConfig?.title ?? defaultTitle

  const subtitle = canLEREOverride ? overrideSubtitle ?? heroCarouselConfig?.subtitle : heroCarouselConfig?.subtitle
  const subtitleActionLink = heroCarouselConfig?.subtitleActionLink
  const tasteOfGoldCampaign = useAppSelector(getTasteOfGoldPromoCampaign)
  const isTasteOfGoldActive = !!tasteOfGoldCampaign

  const takeOverLogo = siteTakeoverTheme?.logoRef && !heroCarouselConfig?.logo ? (
    <Image
      image={{ id: siteTakeoverTheme.logoRef }}
      enhancedFormats={false}
      dpr={2}
      width={heroCarouselConfig?.iconDimension?.width ?? 80}
      height={heroCarouselConfig?.iconDimension?.height ?? 80}
    />
  ) : heroCarouselConfig?.logo

  const takeOverImageBackground = siteTakeoverTheme?.backgroundRef ?
      <GenericBackgroundImage
        imageId={siteTakeoverTheme.backgroundRef}
        backgroundStyle={siteTakeoverTheme.backgroundStyle}
        attachments={heroCarouselConfig?.attachments}
      />
    : heroCarouselConfig?.background

  const takeOverMobileImageBackground = siteTakeoverTheme?.mobileBackgroundRef ?
      <GenericBackgroundImage
        imageId={siteTakeoverTheme.mobileBackgroundRef}
        backgroundStyle={siteTakeoverTheme.backgroundStyle}
        attachments={heroCarouselConfig?.attachments}
      />
    : takeOverImageBackground
  const takeOverVideoBackground = heroCarouselConfig?.videoSourceUrl ?
      <GenericVideoBackground
        videoId={heroCarouselConfig.videoSourceUrl}
        thumbnailImageId={heroCarouselConfig.videoThumbnailImageId}
      /> : undefined

  const showFilters = (canLEREOverride ? !overrideOfferList : true) && !!filterOptions?.length
  const { isAnyVariant: isVideoBackgroundEnabled } = useExperiment(Experiments.labnableVideoBackground)
  const isMobile = useScreenSizeOnly('mobile')

  // business logic to show the offer campaign tag label
  const showOfferCampaignTag = !!offerCampaignTagPayload // offer campaign tag payload is valid and enabled
    && !!offerCampaignTagPayload.campaignId // has a valid campaign ID in the payload
    && !offerCampaignTagPayload.disableOnHeroCarousel // campaign tag is not disabled on hero carousel
    && !!overrideOfferList // override offer list is not empty (LERE is on)
    && canLEREOverride // LERE is allowed to override (no campaignId in carousel config)

  // Compute subtitle element based on type and screen size
  const subtitleElement = useMemo(() => {
    if (!subtitle) return null

    if (typeof subtitle === 'string') {
      // String subtitle: keep the campaign destination available alongside its description.
      if (isMobile && subtitleActionLink) {
        return (
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <BodyText variant="large">
              <InlineBodyTextMarkdownRender content={subtitle} fontSize="large" />
            </BodyText>
            <CarouselViewAllLink to={subtitleActionLink} desktopAsTextLink>
              {intl.formatMessage({
                id: 'vwEHJ5zO8eo5nweHB3ZI',
                defaultMessage: 'View all',
                description: 'Hyperlink text to view all offers in the hero carousel',
              })}
            </CarouselViewAllLink>
          </Group>
        )
      }
      return <StyledBodyTextMarkdownRender content={subtitle} fontSize="large" />
    }

    // ReactNode subtitle: render as-is
    return <BodyText variant="large">{subtitle}</BodyText>
  }, [subtitle, isMobile, subtitleActionLink, intl])

  useHomepageOfferCarouselList({
    offerList: sourceOfferList,
    maxDisplayCount,
    recordOnly: true,
  })

  return (
    <CarouselContainer
      paletteType={containerPalette}
      className={clsx(className, { 'rebrand-highlight': isRebrandExclusiveSurface })}
      data-testid="hero-carousel"
    >
      {(takeOverVideoBackground || takeOverImageBackground || takeOverMobileImageBackground) &&
        <>
          <CSSBreakpoint max="mobile">
            <SiteTakeoverBackground>
              {takeOverMobileImageBackground}
            </SiteTakeoverBackground>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <SiteTakeoverBackground>
              {isVideoBackgroundEnabled ?
                takeOverVideoBackground ?? takeOverImageBackground
                : takeOverImageBackground}
            </SiteTakeoverBackground>
          </CSSBreakpoint>
        </>}
      <Sheet paletteType={titleSheetPalette} background="none">
        <Content gap={24}>
          <LayoutContainer>
            <VerticalSpacer gap={16}>
              <Group
                direction="horizontal"
                horizontalAlign="space-between"
                verticalAlign="center"
                gap={16}
              >
                <Group direction="horizontal" gap={16} verticalAlign="center">
                  {/* LEFT logo: */}
                  {(heroCarouselConfig?.iconPlacement?.left?.min || heroCarouselConfig?.iconPlacement?.left?.max || heroCarouselConfig?.logo) && (
                    <CSSBreakpoint
                      min={heroCarouselConfig?.iconPlacement?.left?.min}
                      max={heroCarouselConfig?.iconPlacement?.left?.max}
                      style={{ alignSelf: heroCarouselConfig?.iconPlacement?.left?.align }}
                    >
                      {takeOverLogo}
                    </CSSBreakpoint>
                  )}
                  <VerticalSpacer gap={8}>
                    {overrideOfferListFetching ? <TextLoadingBox typography="heading2" width="15ch" /> : <Heading variant="heading2">{title}</Heading>}
                    {!!subtitle && <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap">
                      {subtitleElement}
                      {showOfferCampaignTag && <>
                        <BodyText variant="large">
                          {intl.formatMessage({
                            id: 'nzBi6TRzUZIKboGhz0A-',
                            defaultMessage: 'Look for',
                            description: 'Text before campaign tag label in the hero carousel',
                          })}
                        </BodyText>
                        <SiteTakeoverCampaignTagLabel campaignTagId={offerCampaignTagPayload.campaignId} onOfferCard />
                      </>}
                      {(!isMobile || typeof subtitle !== 'string') && subtitleActionLink && <CarouselViewAllLink to={subtitleActionLink} desktopAsTextLink>
                        {intl.formatMessage({
                          id: 'vwEHJ5zO8eo5nweHB3ZI',
                          defaultMessage: 'View all',
                          description: 'Hyperlink text to view all offers in the hero carousel',
                        })}
                      </CarouselViewAllLink>}
                    </Group>}
                  </VerticalSpacer>
                  {/* RIGHT logo: */}
                  {(heroCarouselConfig?.iconPlacement?.left?.min || heroCarouselConfig?.iconPlacement?.left?.max) && (
                    <CSSBreakpoint
                      min={heroCarouselConfig?.iconPlacement?.right?.min}
                      max={heroCarouselConfig?.iconPlacement?.right?.max}
                      style={{ alignSelf: heroCarouselConfig?.iconPlacement?.right?.align }}
                    >
                      {takeOverLogo}
                    </CSSBreakpoint>
                  )}
                </Group>
                <Group direction="horizontal" gap={16} verticalAlign="center">
                  {showFilters && !overrideOfferListFetching && <CSSBreakpoint min="desktop">
                    <Sheet paletteType="default">
                      <Group direction="horizontal" gap={8}>
                        {filterOptions?.map(option =>
                          <FilterChip
                            key={option.value}
                            size="medium"
                            selected={option.value === selectedFilter?.value}
                            data-value={option.value}
                            onClick={toggleFilter}
                          >
                            {option.label}
                          </FilterChip>,
                        )}
                      </Group>
                    </Sheet>
                  </CSSBreakpoint>}
                  {(!heroCarouselConfig || isTasteOfGoldActive) && <CarouselViewAllLink to={searchUrl}>
                    {intl.formatMessage({
                      id: 'vwEHJ5zO8eo5nweHB3ZI',
                      defaultMessage: 'View all',
                      description: 'Hyperlink text to view all offers in the hero carousel',
                    })}
                  </CarouselViewAllLink>}
                </Group>
              </Group>
              {showFilters && !overrideOfferListFetching && <CSSBreakpoint max="tablet">
                <Sheet paletteType="default">
                  <Carousel gap={8} snap="start">
                    {filterOptions?.map(option =>
                      <FilterChip
                        key={option.value}
                        size="medium"
                        selected={option.value === selectedFilter?.value}
                        data-value={option.value}
                        onClick={toggleFilter}
                      >
                        {option.label}
                      </FilterChip>,
                    )}
                  </Carousel>
                </Sheet>
              </CSSBreakpoint>}
            </VerticalSpacer>
          </LayoutContainer>
          <Sheet paletteType={carouselSheetPalette} background="none">
            <OfferListCarousel
              overrideOfferList={sourceOfferList}
              filters={filters}
              tileStyle={tileStyle}
              tileSize="medium"
              eagerLoadImages
            >
              <OfferCarouselLookingForSomethingElse to={searchUrl} />
            </OfferListCarousel>
          </Sheet>
        </Content>
      </Sheet>
    </CarouselContainer>
  )
}

export default React.memo(HeroOfferCarouselSection)
